import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, SparklesIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const PerfumeFinder = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const questions = [
    {
      id: 'gender',
      question: 'למי אתה מחפש בושם?',
      icon: '👤',
      options: [
        { value: 'male', label: 'גברים', emoji: '👨' },
        { value: 'female', label: 'נשים', emoji: '👩' },
        { value: 'unisex', label: 'יוניסקס', emoji: '⚧' }
      ]
    },
    {
      id: 'family',
      question: 'איזה משפחת ריחות מדברת אליך?',
      icon: '🌸',
      options: [
        { value: 'floral', label: 'פרחוני', emoji: '🌸', desc: 'רומנטי ונשי' },
        { value: 'fresh', label: 'רענן', emoji: '🌿', desc: 'נקי ומרענן' },
        { value: 'woody', label: 'עצי', emoji: '🪵', desc: 'חם וארצי' },
        { value: 'oriental', label: 'מזרחי', emoji: '🌹', desc: 'אקזוטי ומתובל' },
        { value: 'fruity', label: 'פירותי', emoji: '🍊', desc: 'מתוק ועליז' }
      ]
    },
    {
      id: 'occasion',
      question: 'למה תשתמש בבושם?',
      icon: '🎯',
      options: [
        { value: 'daily', label: 'יומיומי', emoji: '☀️', desc: 'לכל יום' },
        { value: 'work', label: 'עבודה', emoji: '💼', desc: 'סביבה מקצועית' },
        { value: 'evening', label: 'ערב', emoji: '🌙', desc: 'אירועים מיוחדים' },
        { value: 'sport', label: 'ספורט', emoji: '⚽', desc: 'אחרי אימון' }
      ]
    },
    {
      id: 'intensity',
      question: 'איזו עוצמת ניחוח אתה מעדיף?',
      icon: '💪',
      options: [
        { value: 'light', label: 'עדין', emoji: '🕊️', desc: 'עדין ודיסקרטי' },
        { value: 'medium', label: 'בינוני', emoji: '🔸', desc: 'מורגש אך לא מוחצן' },
        { value: 'strong', label: 'חזק', emoji: '💥', desc: 'בולט ומוחצן' }
      ]
    },
    {
      id: 'budget',
      question: 'מה התקציב שלך?',
      icon: '💰',
      options: [
        { value: 'budget', label: 'עד ₪200', emoji: '💵' },
        { value: 'mid', label: '₪200-500', emoji: '💳' },
        { value: 'premium', label: '₪500-1000', emoji: '💎' },
        { value: 'luxury', label: 'מעל ₪1000', emoji: '👑' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const goToNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate results
      calculateResults();
    }
  };

  const goToPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const calculateResults = () => {
    // Simple recommendation algorithm based on answers
    const recommendations = {
      floral_female_daily: ['LUMEL Rose Garden', 'Spring Blossom', 'Jasmine Dreams'],
      fresh_male_work: ['Ocean Breeze', 'Alpine Fresh', 'Citrus Zen'],
      oriental_female_evening: ['Arabian Nights', 'Spice Route', 'Mystic Amber'],
      woody_male_evening: ['Forest King', 'Cedar Woods', 'Sandalwood Reserve']
    };

    const key = `${answers.family}_${answers.gender}_${answers.occasion}`;
    const suggested = recommendations[key] || ['LUMEL Signature', 'Elegant Essence', 'Luxury Collection'];

    setResults({
      title: 'הבשמים המושלמים עבורך!',
      description: `בהתאם להעדפות שלך, מצאנו ${suggested.length} בשמים שמתאימים לך בצורה מושלמת.`,
      products: suggested
    });
  };

  const restart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResults(null);
  };

  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion?.id];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl transition-all">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
                  <button
                    onClick={onClose}
                    className="absolute top-4 left-4 p-2 rounded-full hover:bg-white/20 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  
                  <div className="text-center">
                    <SparklesIcon className="h-12 w-12 mx-auto mb-3" />
                    <h2 className="text-2xl font-bold mb-2">מוצא הבושם המושלם</h2>
                    <p className="text-white/90">ענה על מספר שאלות ואנחנו נמצא את הבושם האידיאלי עבורך</p>
                  </div>

                  {/* Progress bar */}
                  {!results && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span>שאלה {currentStep + 1} מתוך {questions.length}</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <div className="w-full bg-white/30 rounded-full h-2">
                        <div
                          className="bg-white h-2 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-8">
                  {!results ? (
                    <>
                      {/* Question */}
                      <div className="mb-8 text-center">
                        <div className="text-6xl mb-4">{currentQuestion.icon}</div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {currentQuestion.question}
                        </h3>
                      </div>

                      {/* Options */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                        {currentQuestion.options.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleAnswer(currentQuestion.id, option.value)}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 text-right
                              ${answers[currentQuestion.id] === option.value
                                ? 'border-purple-600 bg-purple-50 shadow-lg scale-105'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-white'
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-4xl">{option.emoji}</div>
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">{option.label}</div>
                                {option.desc && (
                                  <div className="text-sm text-gray-600">{option.desc}</div>
                                )}
                              </div>
                              {answers[currentQuestion.id] === option.value && (
                                <div className="text-purple-600">✓</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Navigation */}
                      <div className="flex justify-between gap-4">
                        <button
                          onClick={goToPrevious}
                          disabled={currentStep === 0}
                          className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold
                            disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors
                            flex items-center gap-2"
                        >
                          <ArrowRightIcon className="h-5 w-5" />
                          חזור
                        </button>
                        
                        <button
                          onClick={goToNext}
                          disabled={!isAnswered}
                          className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 
                            text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed
                            hover:shadow-lg transition-all flex items-center justify-center gap-2"
                        >
                          {currentStep === questions.length - 1 ? 'ראה תוצאות' : 'הבא'}
                          <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Results */}
                      <div className="text-center mb-8">
                        <div className="text-6xl mb-4">🎉</div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {results.title}
                        </h3>
                        <p className="text-gray-600">{results.description}</p>
                      </div>

                      <div className="space-y-3 mb-8">
                        {results.products.map((product, index) => (
                          <div
                            key={index}
                            className="p-4 bg-white rounded-lg border-2 border-purple-200 hover:border-purple-400 
                              transition-colors flex items-center justify-between"
                          >
                            <div>
                              <div className="font-semibold text-gray-900">{product}</div>
                              <div className="text-sm text-gray-600">המלצה מס' {index + 1}</div>
                            </div>
                            <Link
                              to={`/products?search=${encodeURIComponent(product)}`}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold
                                hover:bg-purple-700 transition-colors"
                              onClick={onClose}
                            >
                              צפה
                            </Link>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={restart}
                          className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 
                            font-semibold hover:bg-gray-50 transition-colors"
                        >
                          התחל מחדש
                        </button>
                        <Link
                          to="/products"
                          className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 
                            text-white font-semibold hover:shadow-lg transition-all text-center"
                          onClick={onClose}
                        >
                          צפה בכל הבשמים
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PerfumeFinder;

